/* eslint-disable @typescript-eslint/no-explicit-any */
import { TransformConfig } from '../types/Dashboard';
import { formatChartLabels } from './formatChartLabels';

interface TransformedData {
  labels: string[];
  series: Array<{
    name: string;
    data: number[];
    type?: string;
  }>;
}

/**
 * Obtém valor de campo (suporta literal e nested)
 * 1. Tenta acessar como campo literal (ex: item["Iteration/IterationName"])
 * 2. Se não existe, tenta como nested (ex: item.Iteration.IterationName)
 */
const getNestedValue = (item: any, fieldPath: string): any => {
  if (!fieldPath) return undefined;
  
  // 1. Tentar acessar como campo literal primeiro
  if (Object.prototype.hasOwnProperty.call(item, fieldPath)) {
    return item[fieldPath];
  }
  
  // 2. Se contém "/" e não existe como literal, tentar como nested
  if (fieldPath.includes('/')) {
    const parts = fieldPath.split('/');
    let value = item;
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) return undefined;
    }
    return value;
  }
  
  // 3. Campo simples
  return item[fieldPath];
};

/**
 * Ordenação inteligente de labels - tenta detectar números e datas
 */
const smartSort = (labels: string[], rawData: any[], xField: string): string[] => {
  // Criar mapa de label -> dados originais para acesso a campos como EndDate
  const labelMap = new Map<string, any>();
  rawData.forEach(item => {
    const label = String(getNestedValue(item, xField));
    if (!labelMap.has(label)) {
      labelMap.set(label, item);
    }
  });

  return Array.from(labels).sort((a, b) => {
    const itemA = labelMap.get(a);
    const itemB = labelMap.get(b);

    // Tentar ordenar por EndDate se disponível (Azure DevOps Iterations)
    if (xField.includes('Iteration') && itemA && itemB) {
      const parentField = xField.split('/')[0]; // "Iteration"
      const dateA = itemA[parentField]?.EndDate;
      const dateB = itemB[parentField]?.EndDate;
      
      if (dateA && dateB) {
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      }
    }

    // Tentar ordenar numericamente se houver números na string
    const numA = a.match(/\d+/);
    const numB = b.match(/\d+/);
    
    if (numA && numB) {
      const diff = parseInt(numA[0]) - parseInt(numB[0]);
      if (diff !== 0) return diff;
    }

    // Fallback: ordenação alfabética
    return a.localeCompare(b);
  });
};

/**
 * Transforma dados flat da API para formato ECharts
 */
export const transformChartData = (
  rawData: any[],
  config: TransformConfig
): TransformedData => {

  if (!Array.isArray(rawData) || rawData.length === 0) {
    return { labels: [], series: [] };
  }

  // Determinar campos corretos (suportar xAxis/yAxis e xField/yField)
  const xField = config.xAxis || config.xField;
  const yField = config.yAxis || config.yField;
  const groupField = config.groupBy;

  if (!xField || !yField) {
    return { labels: [], series: [] };
  }

  // PRIORIDADE: Tipos que retornam dados prontos com múltiplas séries como campos
  // comparative/stacked/setDifference: dados já transformados pelo backend
  const isStacked = (config.type === 'comparative' || config.type === 'stacked' || config.type === 'setDifference') && config.compareBy && config.series;
  
  if (isStacked) {
    const compareBy = config.compareBy!;
    const seriesFields = config.series!;
    
    // Extrair labels do campo compareBy (removendo duplicatas)
    const labelsSet = new Set<string>();
    rawData.forEach(item => {
      const label = String(getNestedValue(item, compareBy));
      labelsSet.add(label);
    });
    const labels = Array.from(labelsSet);
    
    // Ordenar labels de forma inteligente
    const sortedLabels = smartSort(labels, rawData, compareBy);
    
    // Criar mapa de série -> stack name (para empilhamento com stackGroups)
    const seriesStackMap = new Map<string, string>();
    if (config.stackGroups && (config.type === 'stacked' || config.type === 'setDifference')) {
      config.stackGroups.forEach(group => {
        group.series.forEach(seriesName => {
          seriesStackMap.set(seriesName, group.name);
        });
      });
    }
    
    // Criar séries para cada campo especificado em config.series
    const series = seriesFields.map(seriesName => {
      // Criar mapa label -> valor para esta série
      const valueMap = new Map<string, number>();
      rawData.forEach(item => {
        const label = String(getNestedValue(item, compareBy));
        const value = item[seriesName] || 0;
        valueMap.set(label, value);
      });
      
      // Mapear valores na ordem correta das labels
      let data = sortedLabels.map(label => valueMap.get(label) || 0);
      
      // Converter para percentual se necessário
      if (config.percentage) {
        const totals = sortedLabels.map(label => {
          return seriesFields.reduce((sum, field) => {
            const item = rawData.find(d => String(getNestedValue(d, compareBy)) === label);
            return sum + (item?.[field] || 0);
          }, 0);
        });
        
        data = data.map((value, index) => {
          const total = totals[index];
          return total > 0 ? (value / total) * 100 : 0;
        });
      }
      
      return {
        name: seriesName,
        data,
        stack: seriesStackMap.get(seriesName), // Adiciona stack group se type === 'stacked'
      };
    });
    
    
    // Formatar labels se forem datas
    const formattedLabels = formatChartLabels(sortedLabels);
    
    return {
      labels: formattedLabels,
      series,
    };
  }

  if (!groupField) {
    // Sem agrupamento - série única
    const labels = rawData.map(item => getNestedValue(item, xField));
    const values = rawData.map(item => getNestedValue(item, yField));

    // Formatar labels se forem datas
    const formattedLabels = formatChartLabels(labels);

    return {
      labels: formattedLabels,
      series: [
        {
          name: yField,
          data: values,
        },
      ],
    };
  }

  // Com agrupamento - múltiplas séries
  const groupedData = new Map<string, Map<string, number>>();
  const allLabels = new Set<string>();

  // Agrupar dados
  rawData.forEach(item => {
    const group = getNestedValue(item, groupField);
    const label = getNestedValue(item, xField);
    const value = getNestedValue(item, yField);

    // Skip items com group null/undefined
    if (group === null || group === undefined) return;

    const groupKey = String(group);
    const labelKey = String(label);

    if (!groupedData.has(groupKey)) {
      groupedData.set(groupKey, new Map());
    }

    groupedData.get(groupKey)!.set(labelKey, value);
    allLabels.add(labelKey);
  });

  // Ordenar labels de forma inteligente (datas, números ou alfabético)
  const sortedLabels = smartSort(Array.from(allLabels), rawData, xField);

  // Criar séries — aplica groupLabels (se fornecido) para renomear a série
  // sem afetar o agrupamento dos dados.
  const groupLabels = config.groupLabels ?? {};
  const series = Array.from(groupedData.entries()).map(([groupName, dataMap]) => {
    const data = sortedLabels.map(label => dataMap.get(label) || 0);

    return {
      name: groupLabels[groupName] ?? groupName,
      data,
    };
  });


  // Formatar labels se forem datas
  const formattedLabels = formatChartLabels(sortedLabels);

  return {
    labels: formattedLabels,
    series,
  };
};
